<!-- components/GlobalToast.vue -->
<template>
  <div
    v-for="group in groups"
    :key="group.position"
    class="fixed z-50 w-full pointer-events-none flex"
    :class="[group.position === 'bottom' ? 'bottom-4 flex-col-reverse' : 'top-4 flex-col']"
  >
    <transition-group name="toast-fade">
      <div v-for="toast in group.toasts" :key="toast.id"
        class="pointer-events-auto"
        :class="[toastClass(toast), alignmentClass(toast.alignment), group.position === 'bottom' ? 'mt-2' : 'mb-2']"
        data-testid="toast"
        :data-toast-type="toast.type"
        role="status">
        <!-- v2 design: white card, colored left edge, dismissible -->
        <template v-if="toast.variant === 'card'">
          <svg v-if="toast.type === 'error'" class="shrink-0 mt-0.5 text-rose-500" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 9v4M12 17h.01" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          </svg>
          <svg v-else class="shrink-0 mt-0.5 text-emerald-500" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
            <path d="M8 12.5l2.5 2.5L16 9.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <div class="flex-1 min-w-0">
            <p v-if="toast.title" data-testid="toast-title" class="text-sm font-semibold text-slate-800 dark:text-slate-100 m-0 [overflow-wrap:anywhere]">{{ toast.title }}</p>
            <p v-if="toast.message" data-testid="toast-message" class="text-[13px] text-slate-500 dark:text-slate-400 m-0 mt-0.5 [overflow-wrap:anywhere]">{{ toast.message }}</p>
          </div>
          <button
            data-testid="toast-close"
            aria-label="Dismiss"
            class="shrink-0 flex items-center justify-center w-6 h-6 -mt-0.5 rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
            @click="dismiss(toast.id)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </template>
        <template v-else>

          <div class="-mb-7">
            <svg v-if="toast.type === 'success'" width="26" height="26" viewBox="0 0 26 26" fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path d="M8 14L11 17L18 10" stroke="#10B981" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
              <path
                d="M13 25C19.6274 25 25 19.6274 25 13C25 6.37258 19.6274 1 13 1C6.37258 1 1 6.37258 1 13C1 19.6274 6.37258 25 13 25Z"
                stroke="#10B981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <svg v-else width="26" height="26" viewBox="0 0 28 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15.8043 2.0275L26.7368 21.0112C27.5031 22.3487 26.5131 24 24.9356 24H3.07055C1.49305 24 0.503055 22.3487 1.2693 21.0112L12.2018 2.0275C12.9893 0.6575 15.0168 0.6575 15.8043 2.0275Z"
                stroke="#F43F5E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M14.0029 14.6667V9.66675" stroke="#F43F5E" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
              <path
                d="M14.0029 20.6667C14.8314 20.6667 15.5029 19.9952 15.5029 19.1667C15.5029 18.3383 14.8314 17.6667 14.0029 17.6667C13.1745 17.6667 12.5029 18.3383 12.5029 19.1667C12.5029 19.9952 13.1745 20.6667 14.0029 20.6667Z"
                fill="#F43F5E" />
            </svg>

          </div>
          <div v-if="toast.title" class="flex flex-row text-base font-semibold mb-1">

            <span class="ml-8">{{ toast.title }}</span>
          </div>
          <div class="ml-8 text-sm">{{ toast.message }}</div>
        </template>
      </div>
    </transition-group>
  </div>
</template>

<script setup>
import { inject, computed } from "vue";

const store = inject("toastStore");

// Each toast carries its own position (falls back to the global default set
// in plugins/toast.js), so callers can opt a single toast into "bottom"
// without changing where every other toast in the app renders. Split into
// separate fixed containers per position so top/bottom toasts stack
// independently instead of fighting over one shared position class.
const groups = computed(() => {
  const byPosition = { top: [], bottom: [] };
  for (const toast of store.toasts) {
    const position = toast.position === "bottom" ? "bottom" : "top";
    byPosition[position].push(toast);
  }
  return Object.entries(byPosition)
    .filter(([, toasts]) => toasts.length)
    .map(([position, toasts]) => ({ position, toasts }));
});

function alignmentClass(a) {
  if (a === "left") return "ml-4";
  if (a === "center") return "mx-auto";
  return "mr-4 ml-auto"; // right
}

// The plugin's own timer removes it too — filtering an already-gone id is a no-op.
function dismiss(id) {
  store.toasts = store.toasts.filter((t) => t.id !== id);
}

const typeClass = (t) =>
  t === "error" ? "bg-gray-600" : "bg-gray-600";

function toastClass(toast) {
  if (toast.variant === "card") {
    return [
      "flex items-start gap-3 w-[360px] max-w-[calc(100vw-32px)] pl-4 pr-3 py-3.5 rounded-xl border border-slate-200 dark:border-slate-700 border-l-4 bg-white dark:bg-slate-800 shadow-lg",
      toast.type === "error" ? "border-l-rose-500" : "border-l-emerald-500",
    ];
  }
  return ["p-4 rounded-xl shadow text-white min-w-[260px] max-w-sm", typeClass(toast.type)];
}
</script>

<style scoped>
/* Fade + slight slide */
.toast-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all 0.3s ease;
}

.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
